import { useCallback, useState, useEffect } from 'react';
import { useIntl } from 'react-intl';

import { ParseError } from 'src/services/downloadFileUrl';
import ListColumn from 'src/componentes/NewList/ListColumn';
import Toolbar from 'src/componentes/NewList/Toolbar';
import Results from 'src/componentes/NewList/Results';
import Button from 'src/componentes/button';
import LoadingContainer from 'src/componentes/loading-container';
import {
  getColumns,
  getFilter,
  getData,
  getPermissions,
  exportList,
} from 'src/componentes/NewList';
import MdiIcon from 'src/componentes/mdi-icon';
import { COLUNAS, FILTROS } from 'src/utils/constants';
import { capitalizeFirstLetter } from 'src/utils/string';
import errorHandler from 'src/utils/error-handler';
import useAuth from 'src/hooks/useAuth';
import Actions from './Actions';
import Edit from '../EventoEditView';
import ContentManager from 'src/componentes/content-manager';
import formatDateFns from 'src/utils/formatDateFns';
import { format } from 'date-fns';
import { formatNumber } from 'src/utils/number';
import List from 'src/componentes/list';

function EventoRiscoListView({
  columnsTag,
  filterTag,
  entityTag,
  endpoint,
  defaultSort,
  defaultSortBy,
  selectedFilter,
  openLastFilter,
  parameters,
  configuracao,
}) {
  const intl = useIntl();

  const [updatedItems] = useState([]);
  const [data, setData] = useState([]);
  const [count, setCount] = useState(0);
  const [columns, setColumns] = useState([]);
  const [allColumns, setAllColumns] = useState([]);
  const [permissions, setPermissions] = useState({});
  const [filter, setFilter] = useState({});
  const [isLoading, setLoading] = useState(false);
  const [sortField, setSortField] = useState(defaultSort);
  const [sortFieldBy, setSortFieldBy] = useState(defaultSortBy);
  const [pageSize, setPageSize] = useState(20);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchText, setSearchText] = useState('');

  const { terms: resources, user } = useAuth();
  const { currentDisplay, currentLocale } = formatDateFns(user?.idioma, true);

  const loadColumns = async () => {
    try {
      const currentColumns = await getColumns(
        defaultColumns,
        entityTag,
        columnsTag
      );
      setColumns(currentColumns.columns);
      setAllColumns(currentColumns.allColumns);
    } catch (err) {
      errorHandler(err);
    }
  };

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      const newPermissions = await getPermissions(endpoint, parameters);
      setPermissions(newPermissions);

      let newData = [];

      if (newPermissions.allowView || newPermissions.allowList) {
        await loadColumns();

        let newFilter = selectedFilter;
        if (openLastFilter && filterTag)
          newFilter = await getFilter(filterTag, true);

        setFilter(newFilter);
        let valueFilter = {};
        if (newFilter != null) valueFilter = newFilter.valor;

        const model = {
          ...valueFilter,
          searchText,
        };
        newData = await getData(
          endpoint,
          model,
          sortField,
          sortFieldBy,
          pageSize,
          currentPage - 1,
          parameters
        );
        setData(newData.list);
        setCount(newData.count);
      }
      setLoading(false);
      return newData;
    } catch (err) {
      setLoading(false);
      errorHandler(err);
    }
  }, [
    endpoint,
    parameters,
    searchText,
    pageSize,
    currentPage,
    sortField,
    sortFieldBy,
  ]);

  useEffect(() => {
    loadData();
  }, [loadData, pageSize, currentPage, searchText, sortField, sortFieldBy]);

  const handleSearchChange = useCallback((value) => {
    setSearchText(value);
  });

  const handleColumnsChange = useCallback(async () => {
    await loadColumns();
    await loadData();
  });

  const handleRefresh = useCallback(async () => {
    await loadData();
  });

  const handleChangeCurrentPage = useCallback((newValue) => {
    setCurrentPage(newValue);
  });

  const handleChangePageSize = useCallback((pageSize) => {
    setPageSize(pageSize);
  });

  const handleSortColumn = useCallback((sort) => {
    setSortField(sort);
  });

  const handleSortByColumn = useCallback((sortBy) => {
    setSortFieldBy(sortBy);
  });

  const handleExport = async (fileExtension) => {
    try {
      setLoading(true);

      let valueFilter = {};
      if (filter != null) valueFilter = filter.valor;

      let newSearchModel = {
        searchText,
        ...valueFilter,
      };

      await exportList(
        newSearchModel,
        endpoint,
        sortField,
        currentPage,
        parameters,
        fileExtension
      );
      setLoading(false);
    } catch (err) {
      setLoading(false);
      errorHandler(ParseError(err));
    }
  };

  const handleNew = () =>
    ContentManager.addContent(
      <Edit
        handleClose={handleRefresh}
        configuracao={configuracao}
        parameters={parameters}
      />
    );

  const handleEdit = (item) =>
    ContentManager.addContent(
      <Edit
        item={item}
        handleClose={handleRefresh}
        configuracao={configuracao}
        parameters={parameters}
      />
    );

  const renderImpacto = (item) => {
    if (!item.impacto) return;
    return item.impacto?.nome;
  };

  const renderResposta = (item) => {
    if (!item.respostaOcorrencia) return;
    return item.respostaOcorrencia?.nome;
  };

  const renderActions = (item) => (
    <Actions
      onRefresh={handleRefresh}
      item={item}
      endpoint={endpoint}
      handleOpenEdit={handleEdit}
      parameters={parameters}
    />
  );

  const renderDate = ({ dataOcorrencia }) =>
    dataOcorrencia &&
    format(dataOcorrencia, currentDisplay, { locale: currentLocale });

  const defaultColumns = [
    <ListColumn
      className="text-right"
      valueField=""
      valueFunction={renderActions}
      default
    />,
    <ListColumn
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'idIntegracao' })
      )}
      valueField="idIntegracao"
      default
    />,
    <ListColumn
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'dataOcorrencia' })
      )}
      valueField="dataOcorrencia"
      default
      sortField="dataOcorrencia"
      valueFunction={renderDate}
    />,
    <ListColumn
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'descricaoEvento' })
      )}
      valueField="descricao"
      default
      sortField="descricao"
    />,
    <ListColumn
      headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'impacto' }))}
      valueField="impacto"
      default
      sortField="impacto"
      valueFunction={renderImpacto}
      visible={configuracao.avaliarImpactoEventoRisco}
    />,
    <ListColumn
      headerText={capitalizeFirstLetter(
        intl.formatMessage({ id: 'labelImpactoFinanceiro' })
      )}
      valueField="impactoFinanceiro"
      valueFunction={(item) =>
        item.impactoFinanceiro ? (
          <div style={{ minWidth: 100 }}>
            {configuracao?.unidadeMedidaImpactoFinanceiro?.sigla}{' '}
            {formatNumber(item.impactoFinanceiro, 2)}
          </div>
        ) : (
          <>-</>
        )
      }
      default
    />,
    <ListColumn
      headerText={capitalizeFirstLetter(intl.formatMessage({ id: 'resposta' }))}
      valueField="resposta"
      valueFunction={renderResposta}
      default
      visible={configuracao.utilizarCampoRespostaEventoRisco}
    />,
  ];

  const actions = !permissions?.allowEdit ? [] : [
    <Button key={1} className="ml-2" onClick={handleNew} type="primary">
      <MdiIcon icon="plus" />
    </Button>,
  ];

  return (
    <LoadingContainer isLoading={isLoading}>
      <Toolbar
        useSavedFilter
        showSearch
        onExport={handleExport}
        onColumnsChange={handleColumnsChange}
        onSearchChange={handleSearchChange}
        onRefresh={handleRefresh}
        searchText={searchText}
        columnsTag={columnsTag}
        columns={columns}
        allColumns={allColumns}
        actions={actions}
      />
      <Results
        resources={resources}
        count={count}
        columns={columns}
        data={data}
        currentPage={currentPage}
        pageSize={pageSize}
        onChangeCurrentPage={handleChangeCurrentPage}
        onChangePageSize={handleChangePageSize}
        onSortColumn={handleSortColumn}
        onSortByColumn={handleSortByColumn}
        sort={sortField}
        sortBy={sortFieldBy}
        updatedItems={updatedItems}
        parameters={parameters}
      />
    </LoadingContainer>
  );
}

EventoRiscoListView.defaultProps = {
  columnsTag: COLUNAS.EVENTO_RISCO,
  filterTag: FILTROS.EVENTO_RISCO,
  endpoint: '/EventoRisco',
  entityTag: null,
  defaultSort: 'DataOcorrencia',
  defaultSortBy: 'desc',
  selectedFilter: null,
  openLastFilter: true,
  parameters: {},
  showToolbar: true,
};

export default EventoRiscoListView;
